import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";

import { TextGetters } from "@app/component/RecordList";
import EntityItem from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityItem";
import EntityRecordView from "@app/scene/map/components/MapMode/components/Sidebar/components/EntityRecordView";
import MultiPinBackButton from "@app/scene/map/components/sidebar/MultiPinBackButton";
import MultiPinRecordList from "@app/scene/map/components/sidebar/MultiPinRecordList";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import { exitLeadFinderMode, hideMapEntityView, hideMultiPin } from "@app/scene/map/store/actions";
import { getFilteredLeadFinderRecords, getMultiPin } from "@app/scene/map/store/selectors";
import { getEntityViewEntityId } from "@app/store/entityView";
import { isLeadFinderBasic } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { leadIdGetter } from "@app/util/map/idGetters";

import Actions from "./components/Actions";
import BasicPlanBanner from "./components/BasicPlanBanner";
import Content from "./components/Content";
import LeadItem from "./components/LeadItem";
import Title from "./components/Title";

interface Props {
  basicPlan: boolean;
  hideMapEntityView: typeof hideMapEntityView;
  hideMultiPin: typeof hideMultiPin;
  isEntityViewVisible: boolean;
  multiPin?: MultiPin;
  onExitLeadFinderMode: () => void;
  records: Array<Lead>;
}

const messages = defineMessages({
  count: {
    id: "map.tool.leadFinder.sidebar.recordList.count",
    defaultMessage: "{count, plural, =0 {No results} one {{count} result} other {{count} results}}",
    description: "Lead Finder sidebar title - records count",
  },
  placeholder: {
    id: "map.tool.leadFinder.sidebar.search.placeholder",
    defaultMessage: "Search lead results",
    description: "Lead results - search placeholder",
  },
});

const Sidebar: React.FC<Props> = ({
  basicPlan,
  hideMapEntityView,
  hideMultiPin,
  isEntityViewVisible,
  multiPin,
  onExitLeadFinderMode,
  records,
}) => {
  const intl = useIntl();
  const isMultiPinMode = !!multiPin;

  const handleClose = useCallback(() => {
    if (isMultiPinMode) {
      hideMultiPin();
    } else {
      onExitLeadFinderMode();
    }
  }, [hideMultiPin, isMultiPinMode, onExitLeadFinderMode]);

  const handleEntityRecordClose = useCallback(() => {
    if (isEntityViewVisible) {
      hideMapEntityView();
    } else {
      onExitLeadFinderMode();
    }
  }, [hideMapEntityView, isEntityViewVisible, onExitLeadFinderMode]);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: (count: number) => intl.formatMessage(messages.count, { count }),
    }),
    [intl]
  );

  const [search, setSearch] = useState("");
  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? records.filter((lead) => lead.name.toLowerCase().includes(q)) : records;
  }, [records, search]);

  return (
    <BaseSidebar
      beforeTitle={<MultiPinBackButton />}
      onClose={handleClose}
      title={<Title />}
      visible
      wrapper={isEntityViewVisible}
    >
      <EntityRecordView onClose={handleEntityRecordClose} />

      {!isEntityViewVisible && (
        <>
          {isMultiPinMode ? (
            <MultiPinRecordList itemComponent={EntityItem} />
          ) : (
            <>
              <Content />
              <RecordList<Lead>
                banner={basicPlan ? <BasicPlanBanner /> : undefined}
                idGetter={leadIdGetter}
                itemComponent={LeadItem}
                onSearch={setSearch}
                records={filteredLeads}
                searchActions={<Actions />}
                searchPlaceholder={intl.formatMessage(messages.placeholder)}
                textGetters={textGetters}
              />
            </>
          )}
        </>
      )}
    </BaseSidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  basicPlan: isLeadFinderBasic(state),
  isEntityViewVisible: !!getEntityViewEntityId(state),
  multiPin: getMultiPin(state),
  records: getFilteredLeadFinderRecords(state),
});

const mapDispatchToProps = {
  hideMapEntityView,
  hideMultiPin,
  onExitLeadFinderMode: exitLeadFinderMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
